import { Guid } from "guid-typescript";
import { TenantNotificationSetting } from "./tenant-notification-settings";
import yup from "shared/validation/yupExtensions";

// GENERAL

export interface TenantSettingsState {
    customInvoiceNumberSettings: InvoiceNumberSettingsResponse | null;
    overtimeAndAbsenceSettings: OvertimeAbsenceSettingsResponse | null;
    tenantNotificationSettings: TenantNotificationSetting[] | null;
    isLoading: boolean;
    error?: string;
}

export const initialTenantSettingsState: TenantSettingsState = {
    customInvoiceNumberSettings: null,
    overtimeAndAbsenceSettings: null,
    tenantNotificationSettings: null,
    isLoading: false,
    error: undefined,
};

export interface TenantSettingResponse {
    id: Guid;
    customInvoiceNumberSettings: InvoiceNumberSettingsResponse | null;
    overtimeAndAbsenceSettings: OvertimeAbsenceSettingsResponse | null;
}

// INVOICE NUMBER SETTINGS

export interface InvoiceNumberSettings {
    invoiceNumberPrefix: string;
    startingNumber: number;
    digitCount: number;
    isUsed: boolean;
}

export interface CreateInvoiceNumberSettingsRequest extends InvoiceNumberSettings {
    tenantSettingsId: string;
}

export interface UpdateInvoiceNumberSettingsRequest extends InvoiceNumberSettings {
    id: string;
}

export interface InvoiceNumberSettingsResponse extends InvoiceNumberSettings {
    id: string;
}

export interface InvoiceNumberSettingsState {
    settings: InvoiceNumberSettingsResponse | null;
}

export type InvoiceNumberSettingsFormValues = {
    id?: string;
    invoiceNumberPrefix: string;
    startingNumber: number | null;
    digitCount: number | null;
    isUsed: boolean;
};

export const initInvoiceNumberSettingsFormValues: InvoiceNumberSettingsFormValues = {
  invoiceNumberPrefix: "",
  startingNumber: null,
  digitCount: null,
  isUsed: false,
};

export const initialInvoiceNumberSettingsState: InvoiceNumberSettingsState = {
    settings: null
};

export const InvoiceNumberSettingsModelScheme: yup.ObjectSchema<InvoiceNumberSettingsFormValues> = yup.object().shape({
    id: yup.string().optional(),
    invoiceNumberPrefix: yup.string()
        .noLeadingSpace("The prefix cannot start with a space.")
        .max(20, "The prefix cannot be longer than 20 characters.")
        .defined(),

    startingNumber: yup.number()
        .nullable()
        .transform((value, originalValue) => (originalValue === "" ? null : value))
        .min(1, "The starting number must be greater than 0.")
        .integer("The starting number must be a whole number.")
        .required("The starting number is required.")
        .test(
            "fits-digit-count",
            "The starting number is longer than the selected digit count.",
            function (value) {
            const { digitCount } = this.parent;

            if (value == null || digitCount == null) return true;
            if (!Number.isInteger(digitCount) || digitCount < 1) return true;

            const maxValue = Math.pow(10, digitCount) - 1;
            return value <= maxValue;
            }
        ),
    
    digitCount: yup.number()
        .nullable()
        .transform((value, originalValue) => (originalValue === "" ? null : value))
        .min(1, "The digit count must be greater than 0.")
        .max(30, "The digit count cannot be more than 30.")
        .integer("The digit count must be a whole number.")
        .required("The digit count is required."),

    isUsed: yup.boolean().defined()
});

// OVERTIME ABSENCE SETTINGS

export interface OvertimeAbsenceSettings {
    isPayedEachMonth: boolean;
    isCountedAsOvertime: boolean;
    startTime: string;
    endTime: string;
    allowWorkDuringLeave: boolean;
    hasAdditionalHours: boolean;
    dailyWorkingHours: number;
    holidayOverruleSickLeave: boolean;
}

export interface CreateOvertimeAbsenceSettingsRequest extends OvertimeAbsenceSettings {
    tenantId?: string;
}

export interface UpdateOvertimeAbsenceSettingsRequest extends OvertimeAbsenceSettings {
    id: string;
}

export interface OvertimeAbsenceSettingsResponse extends OvertimeAbsenceSettings {
    id: string;
}

export type OvertimeAbsenceSettingsFormValues = {
    id?: string;
    isPayedEachMonth: boolean;
    isCountedAsOvertime: boolean;
    startTime: string;
    endTime: string;
    allowWorkDuringLeave: boolean;
    hasAdditionalHours: boolean;
    dailyWorkingHours: number | null;
    holidayOverruleSickLeave: boolean;
};

export const initOvertimeAbsenceSettingsFormValues: OvertimeAbsenceSettingsFormValues = {
    isPayedEachMonth: false,
    isCountedAsOvertime: false,
    startTime: '',
    endTime: '',
    allowWorkDuringLeave: false,
    hasAdditionalHours: false,
    dailyWorkingHours: null,
    holidayOverruleSickLeave: false,
};

export interface OvertimeAbsenceSettingsState {
    settings: OvertimeAbsenceSettingsResponse | null;
}

export const initialOvertimeAbsenceSettingsState: OvertimeAbsenceSettingsState = {
    settings: null
};

export const OvertimeAbsenceSettingsModelScheme: yup.ObjectSchema<OvertimeAbsenceSettingsFormValues> = yup.object().shape({
    id: yup.string().optional(),
    isPayedEachMonth: yup.boolean().defined(),
    isCountedAsOvertime: yup.boolean().defined(),
    startTime: yup.string().required("The start time is required."),
    endTime: yup.string().required("The end time is required."),
    allowWorkDuringLeave: yup.boolean().defined(),
    hasAdditionalHours: yup.boolean().defined(),
    dailyWorkingHours: yup.number()
        .nullable()
        .transform((value, originalValue) => (originalValue === "" ? null : value))
        .integer("The daily working hours by law must be a whole number.")
        .min(0, "The daily working hours by law cannot be less than 0.")
        .max(24, "The daily working hours by law cannot be more than 24.")
        .required("The daily working hours by law are required."),
    holidayOverruleSickLeave: yup.boolean().defined(),
});